import type { ReactNode } from 'react';
import { Button, cn, Flex, Text } from '@kognitos/lattice';
import {
  TRIGGER_STATUS,
  type TTriggerStatus,
} from '../../utils/trigger-status-utils';
import { TriggerStatusBadge } from './TriggerStatusBadge';

export { TRIGGER_STATUS, type TTriggerStatus };

export type TTriggerPresentation = 'card' | 'list';

/**
 * Props for the TriggerType component.
 */
export interface ITriggerTypeProps {
  /** Icon element to display at the top of the card */
  icon: ReactNode;
  /** Title text (max 2 lines, then truncated with ellipsis) */
  title: string;
  /** Description text (max 2 lines when status shown, 3 lines otherwise) */
  description: string;
  /** Optional status text to display (e.g., "Active", "Error", "Pending") */
  status?: TTriggerStatus;
  /** Optional className for custom styling */
  className?: string;
  /** Whether the card is currently selected/focused */
  isSelected?: boolean;
  /** Click handler for the card */
  onClick?: () => void;
  /** Whether the card is disabled */
  disabled?: boolean;
  /** Visual treatment for card selectors or compact add-trigger lists */
  presentation?: TTriggerPresentation;
}

/**
 * TriggerType Component
 *
 * A selectable card component that displays a trigger type option with an icon,
 * title, and description. Used for selecting how to trigger an automation.
 *
 * @example
 * ```tsx
 * <TriggerType
 *   icon={<Icon type="TextCursorInput" size="md" className="text-muted-foreground" />}
 *   title="Add Manually"
 *   description="Some description goes here. If too long, it gets truncated"
 *   onClick={() => handleSelect('manual')}
 * />
 * ```
 */
export function TriggerType({
  icon,
  title,
  description,
  status,
  className,
  isSelected = false,
  onClick,
  disabled = false,
  presentation = 'card',
}: ITriggerTypeProps) {
  if (presentation === 'list') {
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'h-auto w-full justify-start gap-3 px-3 py-2 text-left',
          className,
        )}
      >
        <Flex
          align="center"
          justify="center"
          className="size-8 shrink-0 text-muted-foreground"
        >
          {icon}
        </Flex>
        <Flex direction="column" gap={0.5} className="min-w-0">
          <Text level="small" weight="medium">
            {title}
          </Text>
          <Text
            level="small"
            color="muted"
            weight="normal"
            className="whitespace-normal text-xs leading-4"
          >
            {description}
          </Text>
        </Flex>
      </Button>
    );
  }

  return (
    <button
      type="button"
      aria-disabled={disabled}
      onClick={onClick}
      className={cn(
        // Base styles
        'flex flex-col items-start gap-2 justify-between',
        'w-full p-4 rounded-lg border bg-card text-left',
        'w-35 cursor-pointer h-40 shrink-0',
        'transition-all duration-200',
        // Border and shadow states
        'border-border',
        'hover:border-muted-foreground/50',
        // Focus state with outline
        'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-muted-foreground/50',
        // Selected state
        {
          'ring-[3px] ring-muted-foreground/50': isSelected,
          'cursor-default pointer-events-none': !onClick,
        },
        // Disabled state
        { 'pointer-events-none opacity-50': disabled },
        className,
      )}
    >
      {/* Icon */}
      <div className="shrink-0">{icon}</div>

      {/* Content */}
      <div className="flex flex-col gap-1 w-full min-w-0">
        {/* Title - max 2 lines */}
        <Text
          level="small"
          weight="medium"
          className="w-full line-clamp-2 break-words"
        >
          {title}
        </Text>

        <Text
          as="span"
          level="xSmall"
          color="muted"
          ellipsis={{ lines: status ? 2 : 3, tooltip: true }}
          className="w-full leading-4"
          data-testid="trigger-type-description"
        >
          {description}
        </Text>

        {/* Status indicator */}
        {status && <TriggerStatusBadge status={status} minimal />}
      </div>
    </button>
  );
}

TriggerType.displayName = 'TriggerType';
